import { useState } from "react"
import { GenericFoodEntity } from "../Objects/MealsAndDrinks/GenericFoodEntity"
import { EEnableStatusType } from "../Enum/EEnableStatusType"
import { categoryMemory, promotionsMemory } from "../Objects/Memory/Memory"
import { AddtionalEntity } from "../Objects/Addtional/AddtionalEntity"
import { SelectionEntity } from "../Objects/Selection/SelectionEntity"
import { EProductOrderModeType } from "../Enum/EProductOrderModeType"

export const useCreateGenericFoodEntity = (hasNeedKitchenDefault: boolean = false) => {
  const getDefaultNonComboCategoryId = () =>
    categoryMemory.listData.find((category) => category.name.trim().toLowerCase() !== "combos")?.id ?? categoryMemory.firstId()

  const [promotionsIds, setPromotionsIds] = useState<number[]>([])

  const [name, setName] = useState("")

  const [description, setDescription] = useState("")

  const [coast, setCoast] = useState("")

  const [status, setStatus] = useState<EEnableStatusType>(EEnableStatusType.Active)

  const [categoryId, setCategoryId] = useState(getDefaultNonComboCategoryId())

  const [genericFoodEntity, setGenericFoodEntity] = useState<GenericFoodEntity | null>(null)

  const [additionalsAllowed, setAdditionalsAllowed] = useState<AddtionalEntity[]>([])

  const [freeAdditionalsAllowed, setFreeAdditionalsAllowed] = useState<AddtionalEntity[]>([])
  
  const [allowedSelections, setAllowedSelections] = useState<SelectionEntity[]>([])

  const [freeAddtionalsLimit, setFreeAddtionalsLimit] = useState("0")

  const [tags, setTags] = useState<string[]>([])
  
  const [imageAssetIds, setImageAssetIds] = useState<number[]>([])
  
  const [imageUrls, setImageUrls] = useState<string[]>([])
  
  const [imagesChanged, setImagesChanged] = useState(false)

  const [hasStockControl, setHasStockControl] = useState(false)

  const [stockQuantity, setStockQuantity] = useState("")

  const [hasNeedKitchen, setHasNeedKitchen] = useState(hasNeedKitchenDefault)
  
  const [hasPreparationTime, setHasPreparationTime] = useState(false)

  const [preparationTimeMinutes, setPreparationTimeMinutes] = useState("")

  const [isRodizioOnly, setIsRodizioOnly] = useState(false)

  const [productOrderMode, setProductOrderMode] = useState<EProductOrderModeType>(EProductOrderModeType.ImmediateOnly)

  const [reservationAllowedDays, setReservationAllowedDays] = useState<number[]>([])

  const [reservationAllowedTimes, setReservationAllowedTimes] = useState<string[]>([])

  const handleCategoryId = (e: number) => setCategoryId(e)
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)
  const handleCoastChange = (e: React.ChangeEvent<HTMLInputElement>) => setCoast(e.target.value)
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setStatus(e.target.selectedIndex as EEnableStatusType)
  const handleFreeAddtionalsLimitChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFreeAddtionalsLimit(e.target.value)
  const handleHasStockControlChange = (value: boolean) => setHasStockControl(value)
  const handleHasNeedKitchenChange = (value: boolean) => setHasNeedKitchen(value)
  const handleHasPreparationTimeChange = (value: boolean) => setHasPreparationTime(value)
  const handleIsRodizioOnlyChange = (value: boolean) => setIsRodizioOnly(value)
  const handlePreparationTimeMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPreparationTimeMinutes(e.target.value)
  const handleStockQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => setStockQuantity(e.target.value)
  const handleProductOrderModeChange = (value: EProductOrderModeType) => setProductOrderMode(value)

  const handleInitialGenericFoodEntity = (genericFoodEntity: GenericFoodEntity | null | undefined) => {
    if (genericFoodEntity) {
      setPromotionsIds(genericFoodEntity.promotionsIds)
      setGenericFoodEntity(genericFoodEntity)
      setCategoryId(genericFoodEntity.categoryId || getDefaultNonComboCategoryId())
      setName(genericFoodEntity.name)
      setDescription(genericFoodEntity.description)
      setCoast(genericFoodEntity.valueBase.toString())
      setStatus(genericFoodEntity.status)
      setAdditionalsAllowed(genericFoodEntity.allowedAddtionals)
      setFreeAdditionalsAllowed(genericFoodEntity.freeAllowedAddtionals ?? [])
      setAllowedSelections(genericFoodEntity.allowedSelections ?? [])
      setFreeAddtionalsLimit((genericFoodEntity.freeAddtionalsLimit ?? 0).toString())
      setTags(genericFoodEntity.tags ?? [])
      setImageAssetIds(genericFoodEntity.imageAssetIds ?? [])
      setImageUrls(genericFoodEntity.imageUrls ?? (genericFoodEntity.iconUrl ? [genericFoodEntity.iconUrl] : []))
      setImagesChanged(genericFoodEntity.id === -1)
      setHasStockControl(genericFoodEntity.hasStockControl)
      setStockQuantity(genericFoodEntity.stockQuantity.toString())
      setHasNeedKitchen(genericFoodEntity.needKitchen)
      setHasPreparationTime(genericFoodEntity.hasPreparationTime)
      setPreparationTimeMinutes((genericFoodEntity.preparationTimeMinutes ?? 0).toString())
      setIsRodizioOnly(genericFoodEntity.isRodizioOnly ?? false)
      setProductOrderMode(genericFoodEntity.productOrderMode ?? EProductOrderModeType.ImmediateOnly)
      setReservationAllowedDays(genericFoodEntity.reservationAllowedDays ?? [])
      setReservationAllowedTimes((genericFoodEntity.reservationAllowedTimes ?? []).map((time) => time.toShortTime()))

      return
    }

    setPromotionsIds([])
    setGenericFoodEntity(null)
    setCategoryId(getDefaultNonComboCategoryId())
    setName("")
    setDescription("")
    setCoast("")
    setStatus(EEnableStatusType.Active)
    setAdditionalsAllowed([])
    setFreeAdditionalsAllowed([])
    setAllowedSelections([])
    setFreeAddtionalsLimit("0")
    setTags([])
    setImageAssetIds([])
    setImageUrls([])
    setImagesChanged(false)
    setHasStockControl(false)
    setStockQuantity("")
    setHasNeedKitchen(hasNeedKitchenDefault)
    setHasPreparationTime(false)
    setPreparationTimeMinutes("")
    setIsRodizioOnly(false)
    setProductOrderMode(EProductOrderModeType.ImmediateOnly)
    setReservationAllowedDays([])
    setReservationAllowedTimes([])
  }

  const handleGenericFoodEntity = () => {
    return new GenericFoodEntity({
      id: genericFoodEntity?.id,
      status: status,
      valueBase: Number(coast),
      name: name,
      categoryId: categoryId,
      category: categoryMemory.findById(categoryId),
      description: description,
      promotionsIds: promotionsIds,
      promotions: promotionsMemory.getByIds(promotionsIds),
      allowedAddtionals: additionalsAllowed,
      freeAllowedAddtionals: freeAdditionalsAllowed,
      allowedSelections: allowedSelections,
      freeAddtionalsLimit: Number(freeAddtionalsLimit || 0),
      tags: tags,
      imageAssetIds,
      imageUrls,
      imagesChanged,
      hasStockControl: hasStockControl,
      stockQuantity: Number(stockQuantity || 0),
      needKitchen: hasNeedKitchen,
      hasPreparationTime: hasPreparationTime,
      preparationTimeMinutes: hasPreparationTime ? Number(preparationTimeMinutes || 0) : 0,
      isRodizioOnly: isRodizioOnly,
      productOrderMode: productOrderMode,
      reservationAllowedDays: reservationAllowedDays,
      reservationAllowedTimes: reservationAllowedTimes,
    })
  }

  return {
    name,
    description,
    status,
    coast,
    categoryId,
    promotionsIds,
    additionalsAllowed,
    freeAdditionalsAllowed,
    allowedSelections,
    freeAddtionalsLimit,
    tags,
    imageAssetIds,
    imageUrls,
    imagesChanged,
    hasStockControl,
    stockQuantity,
    hasNeedKitchen,
    hasPreparationTime,
    preparationTimeMinutes,
    isRodizioOnly,
    productOrderMode,
    reservationAllowedDays,
    reservationAllowedTimes,
    setPromotionsIds,
    setTags,
    setImageAssetIds,
    setImageUrls,
    setImagesChanged,
    setAdditionalsAllowed,
    setFreeAdditionalsAllowed,
    setAllowedSelections,
    handleNameChange,
    handleCoastChange,
    handleCategoryId,
    handleDescriptionChange,
    handleStatusChange,
    handleFreeAddtionalsLimitChange,
    handleHasStockControlChange,
    handleHasNeedKitchenChange,
    handleHasPreparationTimeChange,
    handlePreparationTimeMinutesChange,
    handleIsRodizioOnlyChange,
    handleStockQuantityChange,
    handleProductOrderModeChange,
    setReservationAllowedDays,
    setReservationAllowedTimes,
    handleGenericFoodEntity,
    handleInitialGenericFoodEntity,
  }
}
